import { useState, useEffect, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, LogOut, ChevronDown, ChevronRight, Shield, UserCog,
  MapPin, Users, Building2, Clock, Briefcase, Truck, Cpu, Monitor, Radio,
  FileBarChart, Settings, UserCheck, Cog, Lock, UserPlus, AlertTriangle, X, Hand, Phone, MessageCircle, Check, StickyNote, Plus, Pencil, Trash2, Car, History, Search, FileText, Calendar, ClipboardList, Server, Bell, Hash, User, Video, ShieldOff, UserX, ScanFace
} from "lucide-react";
import clienteApi from "@/lib/clienteApi";
import { getPermissoes } from "@/lib/permissoes";
import AuditoriaModal from "./AuditoriaModal";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { ContentFallback } from "@/components/RouteFallback";
import RelatorioAutorizacoesPorAutorizadorModal from "./cliente/RelatorioAutorizacoesPorAutorizadorModal";
import PerfilVisitanteModal from "./cliente/PerfilVisitanteModal";
import NavCadastrosMenu from "./cliente/NavCadastrosMenu";
import ConfigServidorModal from "./cliente/ConfigServidorModal";
import NavRelatoriosMenu from "./cliente/NavRelatoriosMenu";
import SenhaTecnicaModal from "./cliente/SenhaTecnicaModal";
import EventosAlarmesBellModal from "./cliente/EventosAlarmesBellModal";
import VeiculoPesquisaModal from "@/pages/cliente/VeiculoPesquisaModal";
import AgendasHojeModal from "./AgendasHojeModal";
import PortariaGuardModal from "./cliente/PortariaGuardModal";
import { applyZoom, getUserZoom } from "@/lib/userZoom";
import { lgpdAcessoLiberado, liberarAcessoLgpd, bloquearAcessoLgpd } from "@/lib/lgpd";
import { APP_VERSION } from "@/lib/version";
import useAutoLogout from "@/hooks/useAutoLogout";

// Referência global para o popup do WhatsApp (compartilhada entre componentes)
let whatsappPopupRef = null;

const openWhatsAppPopup = (url) => {
  // Se já existe um popup aberto, reutiliza
  if (whatsappPopupRef && !whatsappPopupRef.closed) {
    whatsappPopupRef.location.href = url;
    whatsappPopupRef.focus();
  } else {
    // Cria novo popup
    whatsappPopupRef = window.open(
      url,
      "whatsapp_popup",
      "width=10,height=10,left=99999,top=99999"
    );
  }
};

export default function ClienteLayout({ children }) {
  useAutoLogout();  // v3.16 — encerra sessão por inatividade se configurado no tenant
  const navigate = useNavigate();
  const location = useLocation();
  const [cadastrosOpen, setCadastrosOpen] = useState(false);
  const [relatoriosOpen, setRelatoriosOpen] = useState(false);
  const [auditoriaModalOpen, setAuditoriaModalOpen] = useState(false);
  const [relAutorizModalOpen, setRelAutorizModalOpen] = useState(false);
  const [perfilVisitanteOpen, setPerfilVisitanteOpen] = useState(false);
  const [auditoriaModulo, setAuditoriaModulo] = useState("moradores");
  const [configServidorOpen, setConfigServidorOpen] = useState(false);
  const [closeTimer, setCloseTimer] = useState(null);
  const clienteData = JSON.parse(sessionStorage.getItem("brave_cliente_data") || "{}");
  const operadorData = JSON.parse(sessionStorage.getItem("brave_operador_data") || "null");
  const permissoes = JSON.parse(sessionStorage.getItem("brave_permissoes") || '{"admin":true,"controladores":true,"relatorios":true}');
  const isMaster = !operadorData; // Se não tem operador, é master
  // Fase 2 — Permissões granulares
  const perms = getPermissoes();

  // Estado do monitoramento
  const [monitoradosCount, setMonitoradosCount] = useState(0);

  // Portaria Guard — controla bloqueio de UI enquanto não houver portaria selecionada
  const [bloquearOperacoes, setBloquearOperacoes] = useState(() => !!window.__bravePortariaBloqueada);
  useEffect(() => {
    const h = (e) => setBloquearOperacoes(!!(e?.detail?.bloquear));
    window.addEventListener("brave:portaria-bloqueio-changed", h);
    return () => window.removeEventListener("brave:portaria-bloqueio-changed", h);
  }, []);
  // Classes de disable visual+funcional para topbar e conteúdo principal
  const blockCls = bloquearOperacoes ? "pointer-events-none opacity-40" : "";
  const [showMonitoradosModal, setShowMonitoradosModal] = useState(false);
  const [visitantesMonitorados, setVisitantesMonitorados] = useState([]);
  const [loadingMonitorados, setLoadingMonitorados] = useState(false);

  // v0.5.5 — Badge de Eventos & Alarmes (replicação BSS-Edge)
  const [eventoalarmeCount, setEventoalarmeCount] = useState(0);
  const [showEventoalarmeModal, setShowEventoalarmeModal] = useState(false);

  // Última placa pesquisada — origem: busca manual OU handoff do BSS-Portaria
  const [ultimaPlaca, setUltimaPlaca] = useState(sessionStorage.getItem("brave_ultima_placa") || "");
  const [placaCopiadaMeta, setPlacaCopiadaMeta] = useState(() => {
    try {
      const raw = localStorage.getItem("brave_placa_copiada");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    const handler = () => {
      setUltimaPlaca(sessionStorage.getItem("brave_ultima_placa") || "");
      try {
        const raw = localStorage.getItem("brave_placa_copiada");
        setPlacaCopiadaMeta(raw ? JSON.parse(raw) : null);
      } catch { setPlacaCopiadaMeta(null); }
    };
    window.addEventListener("brave_placa_update", handler);
    return () => window.removeEventListener("brave_placa_update", handler);
  }, []);

  // Aplica o zoom da tela salvo por usuário (chave inclui codigo_origem + login_cliente).
  // Reage a mudanças via evento custom "brave_user_zoom_update".
  useEffect(() => {
    applyZoom(getUserZoom());
    const onZoomChange = () => applyZoom(getUserZoom());
    window.addEventListener("brave_user_zoom_update", onZoomChange);
    return () => window.removeEventListener("brave_user_zoom_update", onZoomChange);
  }, []);

  // Rastrear último input/textarea focado para colar placa
  useEffect(() => {
    const trackFocus = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        window.__braveLastFocusedInput = e.target;
      }
    };
    document.addEventListener("focusin", trackFocus);
    return () => document.removeEventListener("focusin", trackFocus);
  }, []);

  // Estado da lista de espera (aguardando)
  const [aguardandoCount, setAguardandoCount] = useState(0);
  const [showAguardandoModal, setShowAguardandoModal] = useState(false);
  const [gruposAguardando, setGruposAguardando] = useState([]);
  const [loadingAguardando, setLoadingAguardando] = useState(false);
  const [collapsiblesAbertos, setCollapsiblesAbertos] = useState({});

  // Estado das Notas (Post-it)
  const [notesCount, setNotesCount] = useState(0);
  const [showNotesModal, setShowNotesModal] = useState(false);
  // Agendas (badge global na barra superior + modal compartilhado)
  const [agendasCount, setAgendasCount] = useState(0);
  const [agendasVencidasInit, setAgendasVencidasInit] = useState(false);
  const [showAgendasModal, setShowAgendasModal] = useState(false);
  const [showBuscaModal, setShowBuscaModal] = useState(false);
  // LGPD — destravamento da exibição completa de Nome/CPF via senha técnica.
  const [lgpdModal, setLgpdModal] = useState(false);
  const [lgpdLiberado, setLgpdLiberado] = useState(() => lgpdAcessoLiberado());
  useEffect(() => {
    const refresh = () => setLgpdLiberado(lgpdAcessoLiberado());
    window.addEventListener("brave-lgpd-unlock-changed", refresh);
    // Reverifica a cada 60s (cobre expiração do TTL de 15min sem reload)
    const i = setInterval(refresh, 60000);
    return () => { window.removeEventListener("brave-lgpd-unlock-changed", refresh); clearInterval(i); };
  }, []);
  const handleLgpdClick = () => {
    if (lgpdLiberado) {
      // Já liberado → clicar de novo bloqueia (toggle), sinaliza ao usuário.
      bloquearAcessoLgpd();
      setLgpdLiberado(false);
    } else {
      setLgpdModal(true);
    }
  };
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNoteText, setNewNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [notesSearch, setNotesSearch] = useState("");
  // Filtro case-insensitive em texto + nome do operador
  const filteredNotes = (() => {
    const q = notesSearch.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) =>
      (n.texto || "").toLowerCase().includes(q) ||
      (n.operador_nome || "").toLowerCase().includes(q)
    );
  })();

  // Aplicar tema de cores ao carregar
  useEffect(() => {
    const savedColorTheme = localStorage.getItem("brave_color_theme") || "azul";
    document.body.setAttribute("data-theme", savedColorTheme);
    
    // Aplicar tema claro/escuro
    const savedTheme = localStorage.getItem("brave_theme");
    if (savedTheme === "light") {
      document.documentElement.classList.add("light");
      document.documentElement.classList.remove("dark");
    } else {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    }
  }, []);

  // Polling para verificar visitantes monitorados expirados a cada 1 minuto
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await clienteApi.get("/cliente/visitantes/monitorados/count");
        setMonitoradosCount(res.data.count || 0);
      } catch {
        // Silenciar erro
      }
    };

    // Buscar imediatamente
    fetchCount();

    // Configurar intervalo de 1 minuto (60000ms)
    const interval = setInterval(fetchCount, 60000);

    return () => clearInterval(interval);
  }, []);

  // Polling para verificar visitantes aguardando a cada 1 minuto
  useEffect(() => {
    const fetchAguardandoCount = async () => {
      try {
        const res = await clienteApi.get("/cliente/visitantes/aguardando/count");
        setAguardandoCount(res.data.count || 0);
      } catch {
        // Silenciar erro
      }
    };

    // Buscar imediatamente
    fetchAguardandoCount();

    // Configurar intervalo de 1 minuto (60000ms)
    const intervalAguardando = setInterval(fetchAguardandoCount, 60000);

    return () => clearInterval(intervalAguardando);
  }, []);

  // Polling para verificar notas a cada 10 segundos
  useEffect(() => {
    const fetchNotesCount = async () => {
      try {
        const res = await clienteApi.get("/cliente/notes/count");
        setNotesCount(res.data.count || 0);
      } catch {
        // Silenciar erro
      }
    };

    // Buscar imediatamente
    fetchNotesCount();

    // Configurar intervalo de 10 segundos
    const intervalNotes = setInterval(fetchNotesCount, 10000);

    return () => clearInterval(intervalNotes);
  }, []);

  // v0.5.5 — Polling do badge de Eventos & Alarmes (a cada 30 s)
  useEffect(() => {
    let cancelled = false;
    // Bipe Web Audio API (mesmo perfil do alarme BSS-Edge no Dashboard).
    // Toca quando o badge sobe de 0 → N (edge-triggered), evitando ruído contínuo.
    let audioCtx;
    const playBeep = () => {
      try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        // Padrão: 2 bipes curtos (880Hz) p/ chamar atenção sem incomodar.
        for (let i = 0; i < 2; i++) {
          const t0 = audioCtx.currentTime + i * 0.5;
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.connect(gain);
          gain.connect(audioCtx.destination);
          osc.frequency.value = 880;
          osc.type = "sine";
          gain.gain.setValueAtTime(0.0001, t0);
          gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
          osc.start(t0);
          osc.stop(t0 + 0.4);
        }
      } catch { /* usuário ainda não interagiu com a página */ }
    };
    let prevCount = 0;
    const fetchAlarmes = async () => {
      try {
        const r = await clienteApi.get("/cliente/eventoalarmes/badge");
        if (cancelled) return;
        const total = r.data.total || 0;
        if (prevCount === 0 && total > 0) playBeep();
        prevCount = total;
        setEventoalarmeCount(total);
      } catch { /* silencioso */ }
    };
    fetchAlarmes();
    const itv = setInterval(fetchAlarmes, 30000);
    const onRefresh = () => fetchAlarmes();
    window.addEventListener("eventoalarme-refresh", onRefresh);
    return () => {
      cancelled = true;
      clearInterval(itv);
      window.removeEventListener("eventoalarme-refresh", onRefresh);
      try { audioCtx && audioCtx.close(); } catch { /* */ }
    };
  }, []);

  // Polling de contagem de agendas (a cada 30s) + listener para abrir modal via evento global
  useEffect(() => {
    const fetchAgendasCount = async () => {
      try {
        const res = await clienteApi.get("/cliente/agendas/count-hoje");
        const count = res.data.count || 0;
        setAgendasCount(count);
        // Notifica outras telas (ex.: ClienteDashboardPage) para sincronizar o badge
        window.dispatchEvent(new CustomEvent("agendas-count-updated", { detail: { count } }));
      } catch {
        // Silenciar erro
      }
      try {
        const rv = await clienteApi.get("/cliente/agendas/count-vencidas");
        const vcount = rv.data?.count || 0;
        window.dispatchEvent(new CustomEvent("agendas-vencidas-updated", { detail: { count: vcount } }));
      } catch { /* silent */ }
    };
    fetchAgendasCount();
    const intervalAg = setInterval(fetchAgendasCount, 30000);
    const handleOpen = (e) => { setAgendasVencidasInit(!!(e?.detail?.vencidas)); setShowAgendasModal(true); };
    const handleRefresh = () => fetchAgendasCount();
    window.addEventListener("open-agendas-modal", handleOpen);
    window.addEventListener("agendas-refresh", handleRefresh);
    return () => {
      clearInterval(intervalAg);
      window.removeEventListener("open-agendas-modal", handleOpen);
      window.removeEventListener("agendas-refresh", handleRefresh);
    };
  }, []);

  // Fechar modal de Notas com ESC
  useEffect(() => {
    if (!showNotesModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setShowNotesModal(false);
        setEditingNote(null);
        setNewNoteText("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showNotesModal]);

  // Função para abrir o modal de monitorados
  const handleOpenMonitoradosModal = async () => {
    setShowMonitoradosModal(true);
    setLoadingMonitorados(true);
    try {
      const res = await clienteApi.get("/cliente/visitantes/monitorados");
      setVisitantesMonitorados(res.data || []);
    } catch {
      setVisitantesMonitorados([]);
    } finally {
      setLoadingMonitorados(false);
    }
  };

  // Função para baixar visitante
  const handleBaixarVisitante = async (visitanteId) => {
    try {
      await clienteApi.post(`/cliente/visitantes/${visitanteId}/baixar`);
      // Atualizar a lista
      setVisitantesMonitorados(prev => prev.filter(v => v.id !== visitanteId));
      setMonitoradosCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silenciar erro
    }
  };

  // Função para abrir o modal de aguardando
  const handleOpenAguardandoModal = async () => {
    setShowAguardandoModal(true);
    setLoadingAguardando(true);
    try {
      const res = await clienteApi.get("/cliente/visitantes/aguardando");
      setGruposAguardando(res.data || []);
    } catch {
      setGruposAguardando([]);
    } finally {
      setLoadingAguardando(false);
    }
  };

  // Função para autorizar visitante
  const handleAutorizarVisitante = async (visitanteId) => {
    if (!window.confirm("Visitante foi autorizado?")) return;
    
    try {
      await clienteApi.post(`/cliente/visitantes/${visitanteId}/autorizar`);
      // Atualizar a lista removendo o visitante autorizado
      setGruposAguardando(prev => {
        const novosGrupos = prev.map(grupo => ({
          ...grupo,
          visitantes: grupo.visitantes.filter(v => v.id !== visitanteId)
        })).filter(grupo => grupo.visitantes.length > 0);
        return novosGrupos;
      });
      setAguardandoCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silenciar erro
    }
  };

  // Toggle collapsible
  const toggleCollapsibleAguardando = (localId) => {
    setCollapsiblesAbertos(prev => ({
      ...prev,
      [localId]: !prev[localId]
    }));
  };

  // Calcular tempo de espera
  const calcularTempoEspera = (dataCadastro) => {
    if (!dataCadastro) return "-";
    try {
      const cadastro = new Date(dataCadastro);
      const agora = new Date();
      const diffMs = agora - cadastro;
      const diffMinutos = Math.floor(diffMs / 60000);
      
      if (diffMinutos < 60) {
        return `${diffMinutos} min`;
      }
      
      const diffHoras = Math.floor(diffMinutos / 60);
      if (diffHoras < 24) {
        return `${diffHoras}h ${diffMinutos % 60}min`;
      }
      
      const diffDias = Math.floor(diffHoras / 24);
      return `${diffDias}d ${diffHoras % 24}h`;
    } catch {
      return "-";
    }
  };

  // Abrir WhatsApp para ligar para o local
  const handleWhatsAppLocal = (ddi, telefone) => {
    if (!telefone) return;
    const telefoneNumeros = telefone.replace(/\D/g, "");
    const ddiNumeros = (ddi || "+55").replace(/\D/g, "");
    const whatsappUrl = `https://wa.me/${ddiNumeros}${telefoneNumeros}`;
    openWhatsAppPopup(whatsappUrl);
  };

  // Formatar data/hora
  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "-";
    }
  };

  // === Funções das Notas ===
  const handleOpenNotesModal = async () => {
    setShowNotesModal(true);
    setLoadingNotes(true);
    try {
      const res = await clienteApi.get("/cliente/notes");
      setNotes(res.data || []);
    } catch {
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleSaveNote = async () => {
    if (!newNoteText.trim()) return;
    setSavingNote(true);
    try {
      if (editingNote) {
        // Atualizar nota existente
        await clienteApi.put(`/cliente/notes/${editingNote.id}`, { texto: newNoteText.trim() });
        setNotes(prev => prev.map(n => n.id === editingNote.id ? { ...n, texto: newNoteText.trim() } : n));
      } else {
        // Criar nova nota
        const res = await clienteApi.post("/cliente/notes", { texto: newNoteText.trim() });
        setNotes(prev => [res.data, ...prev]);
        setNotesCount(prev => prev + 1);
      }
      setNewNoteText("");
      setEditingNote(null);
    } catch {
      // Silenciar erro
    } finally {
      setSavingNote(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNewNoteText(note.texto);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Deseja excluir esta nota?")) return;
    try {
      await clienteApi.delete(`/cliente/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      setNotesCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silenciar erro
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setNewNoteText("");
  };

  const handleMenuEnter = (setOpen, otherSetOpen) => {
    if (closeTimer) clearTimeout(closeTimer);
    otherSetOpen(false);
    setOpen(true);
  };

  const handleMenuLeave = (setOpen) => {
    const timer = setTimeout(() => setOpen(false), 150);
    setCloseTimer(timer);
  };

  const toggleMenu = (open, setOpen, otherSetOpen) => {
    if (closeTimer) clearTimeout(closeTimer);
    otherSetOpen(false);
    setOpen(!open);
  };

  const nLocal = clienteData?.nomenclatura_local || "Locais de Acesso";

  const handleLogout = () => {
    sessionStorage.removeItem("brave_cliente_token");
    sessionStorage.removeItem("brave_cliente_data");
    sessionStorage.removeItem("brave_operador_data");
    sessionStorage.removeItem("brave_permissoes");
    bloquearAcessoLgpd(); // LGPD volta a ficar bloqueado no próximo login
    // Redirecionar para a tela de login correta
    if (isMaster) {
      navigate("/cliente");
    } else {
      navigate("/operacao");
    }
  };

  const handlePerfilClick = () => {
    // Se for operador, vai para perfil de operador
    if (!isMaster) {
      navigate("/operacao/perfil");
    } else {
      navigate("/cliente/perfil");
    }
  };

  const isActive = (path) => location.pathname === path;
  const isInPath = (segment) => location.pathname.includes(segment);

  const cadastroItems = [];

  // Tipos que NÃO têm "Moradores" (apenas funcionários):
  //   Empresa, Condomínio Comercial
  // Tipos que têm AMBOS "Moradores" + "Funcionários":
  //   Loteamento, Condomínio Residencial
  const tipoCliente = (clienteData?.tipo_cliente || "").toLowerCase();
  const temMoradores = tipoCliente.includes("residencial") || tipoCliente.includes("loteamento");
  // Após unificação, o label padrão de cadastros fixos é "Usuários Fixos"
  // (englobando Moradores + Funcionários + Terceiros).
  const nPessoas = "Usuários Fixos";

  const adminSubItems = [
    perms.p_cad_locais && { label: "Locais de Acesso", path: "/cliente/locais", icon: MapPin },
    perms.p_cad_locais && { label: "EQL-CDC", path: "/cliente/eql", icon: Hash },
    perms.p_cad_setores && { label: "Setores", path: "/cliente/departamentos", icon: Building2 },
    { label: "Portarias", path: "/cliente/portarias", icon: Building2 },
    perms.p_cad_horarios && { label: "Horários", path: "/cliente/horarios", icon: Clock },
    perms.p_cad_operadores && { label: "Operadores", path: "/cliente/operadores", icon: UserPlus },
    // v3.12.6 — Auditoria de Autorizações Órfãs (limpeza de lixo herdado)
    perms.p_config_sistema && { label: "Manutenção", path: "/cliente/manutencao/auditoria-autorizacoes", icon: ShieldOff },
  ].filter(Boolean);

  // Verificar se tem permissão para acessar o submenu
  const hasAdminPermission = isMaster || permissoes.admin;
  // Considera tanto a permissão do operador quanto a flag do admin master
  // (clienteData.exibir_controladores). Por padrão, exibe (legacy/null = true).
  const exibirControladoresAdmin = clienteData?.exibir_controladores !== false;
  const hasControladoresPermission = (isMaster || permissoes.controladores) && exibirControladoresAdmin;
  const hasRelatoriosPermission = isMaster || permissoes.relatorios;

  const controladoresSubItems = [
    { label: "Servidor", onClick: () => setConfigServidorOpen(true), icon: Server, protegido: true },
    { label: "Dispositivos", path: "/cliente/coletores", icon: Radio },
    { label: "CFTV", path: "/cliente/cftv", icon: Video },
    { label: "LPR", path: "/cliente/lpr", icon: Car, protegido: true },
    { label: "Grupos de Acesso", path: "/cliente/cargos", icon: Briefcase },
  ];

  // Subitens do collapsible "Visitantes" — cadastros auxiliares relacionados.
  const visitantesSubItems = [
    { label: "Motivos de Visita", path: "/cliente/motivos-visita", icon: FileText },
    { label: "Empresas Prestadoras", path: "/cliente/prestadores", icon: Truck },
  ];

  // Relatórios "administrativos" — agrupados sob o submenu Admin para o menu
  // de Relatórios não estourar a altura da tela em PCs menores.
  const adminRelItems = [
    perms.p_moradores && { label: nPessoas, path: "/cliente/moradores?imprimir=1", icon: Users },
    perms.p_moradores && { label: "Autorizações", action: "autorizacoes_por_autorizador", icon: Users },
    perms.p_rel_setores && { label: "Setores", path: "/cliente/departamentos?imprimir=1", icon: Building2 },
    perms.p_rel_horarios && { label: "Horários", path: "/cliente/horarios?imprimir=1", icon: Clock },
    // Grupos de Acesso herda do submenu Controladores (regra "g")
    hasControladoresPermission && { label: "Grupos de Acesso", path: "/cliente/relatorios/cargos", icon: Briefcase },
  ].filter(Boolean);

  const relatorioItems = [
    perms.p_rel_consolidado && { label: "Acessos Consolidados", path: "/cliente/relatorios/consolidados", icon: History },
    perms.p_rel_acessos_fixos && { label: "Acessos Fixos", path: "/cliente/relatorios/acessos", icon: FileBarChart },
    perms.p_rel_acessos_fixos && { label: "Faciais", path: "/cliente/relatorios/faciais", icon: ScanFace },
    perms.p_rel_acessos_visitantes && { label: "Acessos de Visitantes", path: "/cliente/relatorios/visitantes", icon: UserPlus },
    perms.p_rel_eventos && { label: "Eventos e Alarmes", path: "/cliente/relatorios/eventos-alarmes", icon: Bell },
    perms.p_rel_placas && { label: "Placas de Veículos", path: "/cliente/relatorios/placas", icon: Car },
    perms.p_rel_locais && { label: "Locais de Acesso", path: "/cliente/locais?imprimir=1", icon: MapPin },
    perms.p_rel_empresas && { label: "Empresas Prestadoras", path: "/cliente/prestadores?imprimir=1", icon: Truck },
    adminRelItems.length > 0 && {
      label: "Admin",
      icon: UserCog,
      children: adminRelItems,
    },
    perms.p_rel_auditoria && {
      label: "Auditoria",
      icon: History,
      children: [
        {
          label: "Cadastros",
          icon: ClipboardList,
          children: [
            { label: nPessoas, modulo: "moradores", icon: Users },
            { label: "Visitantes", modulo: "visitantes", icon: UserPlus },
            { label: "Perfil do Visitante", action: "perfil_visitante", icon: User },
          ],
        },
        { label: "Acesso Manual", path: "/cliente/relatorios/auditoria", icon: History },
        { label: "Revogados", path: "/cliente/relatorios/revogados", icon: UserX },
        { label: "Rotinas Internas", path: "/cliente/relatorios/auditoria?tab=rotinas", icon: Shield },
        { label: "Lista WAF IP", path: "/cliente/relatorios/lista-waf-ip", icon: Shield },
      ],
    },
  ].filter(Boolean);

  return (
    <div data-testid="cliente-layout" className="min-h-screen bg-page-gradient">
      {/* Top Navigation */}
      <nav data-testid="cliente-nav" className="sticky top-0 z-50 w-full bg-nav-gradient shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <button
              data-testid="cliente-nav-logo"
              onClick={() => navigate("/cliente/dashboard")}
              className="flex items-center gap-2 text-white"
            >
              <Shield className="w-6 h-6 text-brand-light hidden [@media(pointer:fine)]:block" />
              <span className="font-heading font-extrabold text-lg tracking-tight text-shadow-logo">
                SGA<span className="hidden [@media(pointer:fine)]:inline"> Brave Access</span>
              </span>
            </button>

            {/* Menu items */}
            <div className="flex items-center gap-1">
              {/* Wrapper de itens bloqueáveis (Dashboard, Cadastros, Relatórios, Busca, Agendas, Notes, Perfil, LGPD)
                  — PortariaSwitcher e Sair ficam fora deste wrapper e permanecem clicáveis. */}
              <div className={`flex items-center gap-1 ${blockCls}`} aria-disabled={bloquearOperacoes}>
              {/* Dashboard - texto oculto em mobile, ícone sempre visível */}
              <button
                data-testid="cliente-nav-dashboard"
                onClick={() => navigate("/cliente/dashboard")}
                className={`flex items-center gap-2 px-2 [@media(pointer:fine)]:px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive("/cliente/dashboard")
                    ? "bg-white/15 text-white"
                    : "text-brand-ice hover:bg-white/10 hover:text-white"
                }`}
                title="Dashboard"
                aria-label="Dashboard"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden [@media(pointer:fine)]:inline">Dashboard</span>
              </button>
              {/* Cadastros Dropdown */}
              <NavCadastrosMenu
                open={cadastrosOpen}
                onMouseEnter={() => handleMenuEnter(setCadastrosOpen, setRelatoriosOpen)}
                onMouseLeave={() => handleMenuLeave(setCadastrosOpen)}
                onToggle={() => toggleMenu(cadastrosOpen, setCadastrosOpen, setRelatoriosOpen)}
                onClose={() => setCadastrosOpen(false)}
                navigate={navigate}
                isActive={isActive}
                isInPath={isInPath}
                hasAdminPermission={hasAdminPermission}
                hasControladoresPermission={hasControladoresPermission}
                adminSubItems={adminSubItems}
                controladoresSubItems={controladoresSubItems}
                visitantesSubItems={visitantesSubItems}
                mostrarControladores={exibirControladoresAdmin}
                temMoradores={temMoradores}
                onAbrirAgendas={() => setShowAgendasModal(true)}
                permMoradores={perms.p_moradores}
                permFuncionarios={perms.p_funcionarios}
                permVisitantes={perms.p_visitantes}
                permAgendas={perms.p_agendas}
              />
              {/* Relatórios Dropdown */}
              <NavRelatoriosMenu
                open={relatoriosOpen}
                onMouseEnter={() => handleMenuEnter(setRelatoriosOpen, setCadastrosOpen)}
                onMouseLeave={() => handleMenuLeave(setRelatoriosOpen)}
                onToggle={() => toggleMenu(relatoriosOpen, setRelatoriosOpen, setCadastrosOpen)}
                onClose={() => setRelatoriosOpen(false)}
                navigate={navigate}
                isActive={isActive}
                isInPath={isInPath}
                hasRelatoriosPermission={hasRelatoriosPermission}
                relatorioItems={relatorioItems}
                onOpenAuditoriaModal={(modulo) => { setAuditoriaModulo(modulo); setAuditoriaModalOpen(true); }}
                onOpenRelAutorizacoes={() => setRelAutorizModalOpen(true)}
                onOpenPerfilVisitante={() => setPerfilVisitanteOpen(true)}
              />


              {/* Busca (abre modal com a mesma tela de /cliente/buscar) */}
              {perms.p_buscar && (
                <button
                  data-testid="cliente-nav-busca"
                  onClick={() => setShowBuscaModal(true)}
                  className="relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-brand-ice hover:bg-white/10 hover:text-white"
                  title="Buscar Cadastro (Placa, Nome ou CPF)"
                >
                  <Search className="w-4 h-4" />
                </button>
              )}

              {/* Agendas de Hoje */}
              {perms.p_agendas && (
                <button
                  data-testid="cliente-nav-agendas"
                  onClick={() => setShowAgendasModal(true)}
                  className="relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-brand-ice hover:bg-white/10 hover:text-white"
                  title="Agendas de Hoje"
                >
                  <Calendar className="w-4 h-4" />
                  {agendasCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-green-500 rounded-full">
                      {agendasCount}
                    </span>
                  )}
                </button>
              )}

              {/* v0.5.5 — Badge de Eventos & Alarmes (BSS-Edge) */}
              {eventoalarmeCount > 0 && (
                <button
                  data-testid="badge-eventoalarme"
                  onClick={() => setShowEventoalarmeModal(true)}
                  className="relative flex items-center justify-center w-10 h-10 rounded-full bg-red-500/20 hover:bg-red-500/30 transition-colors animate-pulse"
                  title={`${eventoalarmeCount} alarme(s) ativo(s) — clique para ver e resetar`}
                >
                  <Bell className="w-5 h-5 text-red-400" />
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-bounce">
                    {eventoalarmeCount}
                  </span>
                </button>
              )}

              {/* Notes (Post-it) */}
              <button
                data-testid="cliente-nav-notes"
                onClick={handleOpenNotesModal}
                className="relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-brand-ice hover:bg-white/10 hover:text-white"
                title="Anotações"
              >
                <StickyNote className="w-4 h-4" />
                Notes
                {notesCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-brand-dark bg-yellow-400 rounded-full">
                    {notesCount}
                  </span>
                )}
              </button>

              {/* Badge de Monitoramento - Piscante */}
              {monitoradosCount > 0 && (
                <button
                  data-testid="badge-monitorados"
                  onClick={handleOpenMonitoradosModal}
                  className="relative flex items-center justify-center w-10 h-10 rounded-full bg-red-500/20 hover:bg-red-500/30 transition-colors animate-pulse"
                  title={`${monitoradosCount} visitante(s) com tempo expirado`}
                >
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full animate-bounce">
                    {monitoradosCount}
                  </span>
                </button>
              )}

              {/* Badge de Aguardando */}
              {aguardandoCount > 0 && (
                <button
                  data-testid="badge-aguardando"
                  onClick={handleOpenAguardandoModal}
                  className="relative flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/20 hover:bg-amber-500/30 transition-colors"
                  title={`${aguardandoCount} visitante(s) aguardando`}
                >
                  <Hand className="w-5 h-5 text-amber-400" />
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-amber-500 rounded-full">
                    {aguardandoCount}
                  </span>
                </button>
              )}

              {/* Separator */}
              <div className="w-px h-6 bg-white/20 mx-2" />

              {/* Última placa pesquisada / copiada do BSS-Portaria */}
              {ultimaPlaca && (
                <button
                  data-testid="badge-ultima-placa"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const el = window.__braveLastFocusedInput;
                    if (el && document.body.contains(el)) {
                      const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
                        || Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
                      if (nativeSetter) {
                        nativeSetter.call(el, ultimaPlaca);
                        el.dispatchEvent(new Event('input', { bubbles: true }));
                        el.dispatchEvent(new Event('change', { bubbles: true }));
                      }
                      el.focus();
                    }
                    navigator.clipboard.writeText(ultimaPlaca).catch(() => {});
                    const toast = document.createElement('div');
                    toast.textContent = `Placa ${ultimaPlaca} colada!`;
                    toast.className = 'fixed top-16 right-4 z-[9999] bg-green-600 text-white text-xs px-3 py-2 rounded-lg shadow-lg';
                    document.body.appendChild(toast);
                    setTimeout(() => toast.remove(), 2000);
                  }}
                  className={
                    "relative flex items-center justify-center w-10 h-10 rounded-full transition-colors " +
                    (placaCopiadaMeta?.placa === ultimaPlaca
                      // Cor de destaque quando veio do BSS-Portaria (handoff)
                      ? "bg-amber-500/25 hover:bg-amber-500/40 ring-2 ring-amber-400/60 animate-pulse"
                      : "bg-green-500/20 hover:bg-green-500/30")
                  }
                  title={
                    placaCopiadaMeta?.placa === ultimaPlaca
                      ? `Placa da Portaria: ${ultimaPlaca}` +
                        (placaCopiadaMeta.cadastrada ? " · JÁ CADASTRADA" : " · NÃO CADASTRADA — escolha Visitante ou Fixo") +
                        `\n(clique para colar no campo focado)`
                      : `Colar placa: ${ultimaPlaca}`
                  }
                >
                  <Car className={
                    "w-5 h-5 " +
                    (placaCopiadaMeta?.placa === ultimaPlaca ? "text-amber-300" : "text-green-400")
                  } />
                </button>
              )}

              {/* Perfil icon */}
              <button
                data-testid="cliente-nav-perfil"
                onClick={handlePerfilClick}
                title="Meu Perfil"
                className={`flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive("/cliente/perfil") || isActive("/operacao/perfil")
                    ? "bg-white/15 text-white"
                    : "text-brand-ice hover:bg-white/10 hover:text-white"
                }`}
              >
                <UserCog className="w-5 h-5" />
              </button>

              {/* LGPD — Acesso completo a dados via senha técnica */}
              {perms.p_lgpd && (
                <button
                  data-testid="cliente-nav-lgpd"
                  onClick={handleLgpdClick}
                  title={lgpdLiberado
                    ? "LGPD: Acesso completo a dados LIBERADO — clique para travar novamente"
                    : "LGPD: Dados anonimizados — clique para liberar acesso completo (senha técnica)"}
                  className={`relative flex items-center gap-1.5 px-2.5 h-10 rounded-lg text-xs font-heading font-bold transition-colors duration-200 border ${
                    lgpdLiberado
                      ? "bg-amber-500/15 border-amber-400/40 text-amber-200 hover:bg-amber-500/25"
                      : "border-transparent text-brand-ice hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {lgpdLiberado ? <Lock className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                  <span className="hidden lg:inline">LGPD</span>
                  {lgpdLiberado && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-brand-medium" />
                  )}
                </button>
              )}
              </div>{/* fim wrapper bloqueável */}

              {/* Logout — sempre disponível, mesmo sem portaria selecionada */}
              <button
                data-testid="cliente-nav-logout"
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-300 hover:bg-red-500/15 hover:text-red-200 transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* O aviso "Sincronização desligada pelo admin" NÃO aparece como faixa
          global — ele é exibido como seção "Sincronização na rede local" dentro
          do collapsible de dados do cliente em /cliente/dashboard. */}

      {/* Main Content — bloqueado visualmente e funcionalmente quando não há portaria selecionada
          (exceto na rota /cliente/portarias, onde o guard libera para permitir cadastro). */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${blockCls}`}>
        <RouteErrorBoundary>
          <Suspense fallback={<ContentFallback />}>
            {children}
          </Suspense>
        </RouteErrorBoundary>
      </main>

      {/* Guard de portaria selecionada */}
      <PortariaGuardModal />

      {/* Footer */}
      <footer className="py-4 text-center">
        <a
          href="https://www.braveaccess.com.br"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-brave-link"
          style={{ textDecoration: "none" }}
          className="text-brand-ice/60 hover:text-brand-light text-xs font-body text-shadow-footer transition-colors no-underline block"
          title="Brave Access"
        >
          SGA Brave Access &copy; {new Date().getFullYear()} &mdash; Desenvolvido por Interaccess Sistemas
        </a>
        <p className="text-brand-ice/50 text-xs font-heading font-bold tracking-wider text-shadow-footer">
          SGA - Sistema de Gerenciamento de Acesso <span className="text-brand-ice/35">- Versão {APP_VERSION}</span>
        </p>
        <a
          href="/politica-privacidade.html"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-politica-privacidade"
          className="text-brand-ice/45 hover:text-brand-light text-[11px] font-body underline-offset-2 hover:underline mt-1 inline-block transition-colors"
        >
          Política de Privacidade · LGPD
        </a>
      </footer>

      {/* Modal LGPD — desbloqueio temporário via senha técnica */}
      <SenhaTecnicaModal
        open={lgpdModal}
        onClose={() => setLgpdModal(false)}
        onSuccess={() => { liberarAcessoLgpd(); setLgpdLiberado(true); }}
        targetLabel="Acesso completo a dados (LGPD)"
      />

      {/* Modal de Visitantes Monitorados */}
      {showMonitoradosModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-dark border border-brand-ice/20 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header do Modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-brand-ice/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold text-brand-ice">
                    Visitantes com Tempo Expirado
                  </h2>
                  <p className="text-xs text-brand-ice/60">
                    {visitantesMonitorados.length} visitante(s) aguardando baixa
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMonitoradosModal(false)}
                className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="flex-1 overflow-auto p-4">
              {loadingMonitorados ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-brand-light border-t-transparent rounded-full animate-spin" />
                </div>
              ) : visitantesMonitorados.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-brand-ice/60">Nenhum visitante com tempo expirado</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {visitantesMonitorados.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-red-500/30"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-brand-ice flex items-center gap-2 flex-wrap">
                          <span>{v.nome}</span>
                          {v.passagem_autorizada && (
                            <span
                              data-testid={`badge-pa-${v.id}`}
                              title="Passagem Autorizada"
                              className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-400/40 font-heading uppercase tracking-wider"
                            >
                              PA
                            </span>
                          )}
                        </p>
                        {v.passagem_autorizada && (() => {
                          // Computa o "tipo" de monitoramento gravado a partir das datas:
                          // - data_cadastro (início da contagem)
                          // - tempo_limite (fim do monitoramento)
                          // Inferimos se foi "minutos", "horas" ou "até HH:MM"
                          const parse = (s) => {
                            if (!s) return null;
                            const d = new Date(s);
                            return Number.isNaN(d.getTime()) ? null : d;
                          };
                          const ini = parse(v.data_cadastro);
                          const fim = parse(v.tempo_limite);
                          let descricao = "monitoramento";
                          if (fim) {
                            if (ini) {
                              const diffMin = Math.round((fim - ini) / 60000);
                              if (diffMin > 0) {
                                if (diffMin % 60 === 0 && diffMin >= 60) {
                                  const h = diffMin / 60;
                                  descricao = `${h} hora${h > 1 ? "s" : ""}`;
                                } else if (diffMin < 60) {
                                  descricao = `${diffMin} minuto${diffMin > 1 ? "s" : ""}`;
                                } else {
                                  const hh = String(fim.getHours()).padStart(2, "0");
                                  const mm = String(fim.getMinutes()).padStart(2, "0");
                                  descricao = `até ${hh}:${mm}h`;
                                }
                              }
                            } else {
                              const hh = String(fim.getHours()).padStart(2, "0");
                              const mm = String(fim.getMinutes()).padStart(2, "0");
                              descricao = `até ${hh}:${mm}h`;
                            }
                          }
                          return (
                            <p
                              data-testid={`pa-descricao-${v.id}`}
                              className="text-[11px] text-amber-300 mt-0.5"
                            >
                              Passagem Autorizada por {descricao}
                            </p>
                          );
                        })()}
                        <div className="flex items-center gap-4 mt-1 text-xs text-brand-ice/60 flex-wrap">
                          <span>CPF: {v.cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") || "-"}</span>
                          <span>Local: {v.local_acesso_nome || "-"}</span>
                          {v.motivo && <span>Tipo: <span className="text-brand-ice/80">{v.motivo}</span></span>}
                          {v.eql_visitada && <span>EQL Visitada: <span className="text-brand-ice/80">{v.eql_visitada}</span></span>}
                          {v.pessoa_visitada_nome && <span>Visitado: <span className="text-brand-ice/80">{v.pessoa_visitada_nome}</span></span>}
                        </div>
                        {(v.placa || v.modelo_veiculo || v.cor_veiculo) && (
                          <div className="flex items-center gap-4 mt-1 text-xs text-brand-ice/60 flex-wrap">
                            {v.placa && <span className="font-mono font-bold text-brand-ice">{v.placa}</span>}
                            {v.modelo_veiculo && <span>{v.modelo_veiculo}</span>}
                            {v.cor_veiculo && <span>· {v.cor_veiculo}</span>}
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-xs">
                          <span className="text-brand-ice/60">
                            Cadastro: {formatDateTime(v.data_cadastro)}
                          </span>
                          <span className="text-brand-ice/60">
                            Limite: {formatDateTime(v.tempo_limite)}
                          </span>
                          <span className="text-red-400 font-medium">
                            +{v.minutos_excedidos || 0} min excedido
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleBaixarVisitante(v.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <Clock className="w-4 h-4" />
                        Baixar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visitantes Aguardando */}
      {showAguardandoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-dark border border-brand-ice/20 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Header do Modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-brand-ice/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Hand className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold text-brand-ice">
                    Visitantes Aguardando
                  </h2>
                  <p className="text-xs text-brand-ice/60">
                    {aguardandoCount} visitante(s) na lista de espera
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAguardandoModal(false)}
                className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="flex-1 overflow-auto p-4">
              {loadingAguardando ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-brand-light border-t-transparent rounded-full animate-spin" />
                </div>
              ) : gruposAguardando.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-brand-ice/60">Nenhum visitante aguardando</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {gruposAguardando.map((grupo) => (
                    <div key={grupo.local_id} className="border border-brand-ice/20 rounded-lg overflow-hidden">
                      {/* Header do Collapsible */}
                      <button
                        onClick={() => toggleCollapsibleAguardando(grupo.local_id)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <MapPin className="w-4 h-4 text-brand-ice/60" />
                          <span className="font-medium text-brand-ice">{grupo.local_nome}</span>
                          {grupo.ramal && (
                            <span className="text-xs text-brand-ice/50 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {grupo.ramal}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="px-2.5 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold rounded-full">
                            {grupo.visitantes.length}
                          </span>
                          {collapsiblesAbertos[grupo.local_id] ? (
                            <ChevronDown className="w-4 h-4 text-brand-ice/60" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-brand-ice/60" />
                          )}
                        </div>
                      </button>

                      {/* Conteúdo do Collapsible */}
                      {collapsiblesAbertos[grupo.local_id] && (
                        <div className="divide-y divide-brand-ice/10">
                          {grupo.visitantes.map((v) => (
                            <div
                              key={v.id}
                              className="flex items-center justify-between px-4 py-3 bg-white/[0.02]"
                            >
                              <div className="flex-1 grid grid-cols-4 gap-4 items-center">
                                {/* Nome */}
                                <div>
                                  <p className="font-medium text-brand-ice text-sm">{v.nome}</p>
                                </div>
                                
                                {/* Ramal */}
                                <div className="text-center">
                                  <p className="text-xs text-brand-ice/50">Ramal</p>
                                  <p className="text-sm text-brand-ice">{grupo.ramal || "-"}</p>
                                </div>
                                
                                {/* Data */}
                                <div className="text-center">
                                  <p className="text-xs text-brand-ice/50">Data</p>
                                  <p className="text-sm text-brand-ice">{formatDateTime(v.data_cadastro)}</p>
                                </div>
                                
                                {/* Tempo de Espera */}
                                <div className="text-center">
                                  <p className="text-xs text-brand-ice/50">Espera</p>
                                  <p className="text-sm text-amber-400 font-medium">{calcularTempoEspera(v.data_cadastro)}</p>
                                </div>
                              </div>
                              
                              {/* Botões de Ação */}
                              <div className="flex items-center gap-2 ml-4">
                                {/* WhatsApp */}
                                {grupo.telefone_whatsapp && (
                                  <button
                                    onClick={() => handleWhatsAppLocal(grupo.telefone_ddi, grupo.telefone_whatsapp)}
                                    className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"
                                    title="Chamar local via WhatsApp"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                  </button>
                                )}
                                
                                {/* Autorizar */}
                                <button
                                  onClick={() => handleAutorizarVisitante(v.id)}
                                  className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors"
                                  title="Autorizar entrada"
                                >
                                  <Check className="w-4 h-4" />
                                  Autorizar
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notas (Post-it) */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-dark border border-brand-ice/20 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Header do Modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-brand-ice/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center">
                  <StickyNote className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold text-brand-ice">
                    Notas
                  </h2>
                  <p className="text-xs text-brand-ice/60">
                    {notesSearch.trim()
                      ? `${filteredNotes.length} de ${notes.length} anotação(ões)`
                      : `${notes.length} anotação(ões) compartilhada(s)`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setShowNotesModal(false); setEditingNote(null); setNewNoteText(""); setNotesSearch(""); }}
                className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Campo de Busca */}
            <div className="px-6 py-3 border-b border-brand-ice/10">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-ice/40 pointer-events-none" />
                <input
                  type="text"
                  value={notesSearch}
                  onChange={(e) => setNotesSearch(e.target.value)}
                  placeholder="Buscar nas notas (placa, nome, texto...)"
                  data-testid="notes-search-input"
                  className="w-full pl-9 pr-9 py-2 rounded-lg bg-white/5 border border-brand-ice/20 text-brand-ice text-sm placeholder:text-brand-ice/40 focus:outline-none focus:border-brand-light"
                />
                {notesSearch && (
                  <button
                    type="button"
                    onClick={() => setNotesSearch("")}
                    title="Limpar busca"
                    data-testid="notes-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-brand-ice/50 hover:text-brand-ice hover:bg-white/10"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Área de Nova Nota / Edição */}
            <div className="px-6 py-4 border-b border-brand-ice/10 bg-white/[0.02]">
              <div className="flex gap-3">
                <textarea
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder="Digite sua anotação..."
                  className="flex-1 px-4 py-3 rounded-lg bg-yellow-100/10 border border-yellow-400/30 text-brand-ice placeholder:text-brand-ice/40 text-sm resize-none focus:outline-none focus:border-yellow-400"
                  rows={2}
                />
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleSaveNote}
                    disabled={!newNoteText.trim() || savingNote}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-brand-dark font-medium text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingNote ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    {savingNote ? "..." : editingNote ? "Atualizar" : "Adicionar"}
                  </button>
                  {editingNote && (
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-brand-ice text-sm rounded-lg transition-colors"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Lista de Notas */}
            <div className="flex-1 overflow-auto p-4">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-8">
                  <StickyNote className="w-12 h-12 text-brand-ice/20 mx-auto mb-3" />
                  <p className="text-brand-ice/60">Nenhuma nota ainda</p>
                  <p className="text-xs text-brand-ice/40 mt-1">Adicione uma anotação acima</p>
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-brand-ice/20 mx-auto mb-3" />
                  <p className="text-brand-ice/60">Nenhuma nota corresponde à busca</p>
                  <p className="text-xs text-brand-ice/40 mt-1">Tente outro termo ou limpe o filtro</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredNotes.map((note) => (
                    <div
                      key={note.id}
                      className="relative p-4 bg-yellow-100/10 border border-yellow-400/20 rounded-lg group"
                    >
                      {/* Conteúdo da Nota */}
                      <p className="text-sm text-brand-ice whitespace-pre-wrap pr-16">{note.texto}</p>
                      
                      {/* Info */}
                      <div className="flex items-center gap-3 mt-3 text-xs text-brand-ice/50">
                        <span>{note.operador_nome}</span>
                        <span>•</span>
                        <span>{formatDateTime(note.data_criacao)}</span>
                      </div>
                      
                      {/* Botões de Ação */}
                      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEditNote(note)}
                          className="p-1.5 rounded bg-white/10 hover:bg-white/20 text-brand-ice transition-colors"
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Auditoria (Relatório) */}
      <AuditoriaModal
        open={auditoriaModalOpen}
        onOpenChange={setAuditoriaModalOpen}
        modulo={auditoriaModulo}
        modo="relatorio"
      />

      {/* Modal de Configuração do Servidor de Acesso (Cadastros > Controladores > Servidor) */}
      <ConfigServidorModal
        open={configServidorOpen}
        onClose={() => setConfigServidorOpen(false)}
      />

      {/* Modal de Busca — reutiliza o componente de /cliente/buscar */}
      <VeiculoPesquisaModal open={showBuscaModal} onOpenChange={setShowBuscaModal} />

      {/* Relatório de Autorizações por Autorizador */}
      <RelatorioAutorizacoesPorAutorizadorModal
        open={relAutorizModalOpen}
        onOpenChange={setRelAutorizModalOpen}
        operatorNome={operadorData?.nome || "Admin (Master)"}
        clienteNome={JSON.parse(sessionStorage.getItem("brave_cliente_data") || "null")?.nome_fantasia || JSON.parse(sessionStorage.getItem("brave_cliente_data") || "null")?.razao_social || ""}
      />

      {/* Perfil do Visitante — Insights de comportamento */}
      <PerfilVisitanteModal
        open={perfilVisitanteOpen}
        onOpenChange={setPerfilVisitanteOpen}
        operatorNome={operadorData?.nome || "Admin (Master)"}
        clienteNome={JSON.parse(sessionStorage.getItem("brave_cliente_data") || "null")?.apelido || JSON.parse(sessionStorage.getItem("brave_cliente_data") || "null")?.razao_social || ""}
      />

      {/* v0.5.5 — Modal de Eventos & Alarmes (badge sino) */}
      <EventosAlarmesBellModal
        open={showEventoalarmeModal}
        onClose={() => setShowEventoalarmeModal(false)}
        onReset={() => window.dispatchEvent(new CustomEvent("eventoalarme-refresh"))}
      />

      {/* Modal Agendas de Hoje (compartilhado: barra superior + dashboard) */}
      <AgendasHojeModal
        open={showAgendasModal}
        initialVencidas={agendasVencidasInit}
        onClose={() => { setShowAgendasModal(false); setAgendasVencidasInit(false); }}
        onConfirmed={async () => {
          try {
            const res = await clienteApi.get("/cliente/agendas/count-hoje");
            setAgendasCount(res.data.count || 0);
          } catch { /* silent */ }
          try {
            const rv = await clienteApi.get("/cliente/agendas/count-vencidas");
            window.dispatchEvent(new CustomEvent("agendas-vencidas-updated", { detail: { count: rv.data?.count || 0 } }));
          } catch { /* silent */ }
        }}
      />
    </div>
  );
}
